import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import {
  catchError,
  finalize,
  map,
  shareReplay,
  switchMap,
  takeUntil,
} from "rxjs/operators";
import {
  AuthService,
  InvalidRefreshResponseError,
} from "./auth.service";
import { SKIP_AUTH_RECOVERY } from "./auth-request.context";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private authService = inject(AuthService);
  private tokenRefresh$: Observable<string> | null = null;

  constructor() {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const isAuthRequest = this.isAuthRequest(request);
    const skipAuthRecovery = request.context.get(SKIP_AUTH_RECOVERY);
    const accessToken = this.authService.getAccessToken();
    if (
      !isAuthRequest &&
      !skipAuthRecovery &&
      accessToken
    ) {
      request = this.addToken(request, accessToken);
    }
    return next.handle(request).pipe(
      catchError((error: any) => {
        console.error("HTTP klaida", {
          url: request.urlWithParams,
          status: error?.status,
          message: error?.message,
          error,
        });

        if (
          error instanceof HttpErrorResponse &&
          error.status === 0 &&
          error.error instanceof ProgressEvent
        ) {
          console.error("HTTP klaida", {
            url: request.urlWithParams,
            status: error?.status,
            message: error?.message,
            error,
          });

          return throwError(
            () =>
              new HttpErrorResponse({
                error: {
                  error: "server_side_error",
                  error_descrip: "Server Side Error",
                },
                status: 404,
              })
          );
        } else if (
          error instanceof HttpErrorResponse &&
          !isAuthRequest &&
          !skipAuthRecovery &&
          error.status === 401 &&
          error.error?.error === "invalid_access_token"
        ) {
          return this.handle401Error(request, next, error);
        } else {
          return throwError(() => error);
        }
      })
    );
  }

  private isAuthRequest(request: HttpRequest<any>): boolean {
    return (
      request.url.includes("/auth/get_tokens") ||
      request.url.includes("/auth/refresh_token")
    );
  }

  private addToken(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        "Content-Type": "text/html",
        "Access-Token": token,
      },
    });
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
    _error: HttpErrorResponse
  ) {
    const failedToken = request.headers.get("Access-Token");
    const currentToken = this.authService.getAccessToken();
    if (failedToken && currentToken && failedToken !== currentToken) {
      return next.handle(this.addToken(request, currentToken));
    }

    return this.getRefreshedAccessToken().pipe(
      switchMap((accessToken) =>
        next.handle(this.addToken(request, accessToken))
      )
    );
  }

  private getRefreshedAccessToken(): Observable<string> {
    if (!this.tokenRefresh$) {
      this.tokenRefresh$ = this.authService.refreshToken().pipe(
        map((token: any) => token.access_token),
        catchError((refreshError) =>
          refreshError instanceof InvalidRefreshResponseError ||
          (refreshError instanceof HttpErrorResponse &&
            refreshError.status === 401 &&
            refreshError.error?.error === "invalid_refresh_token")
            ? this.authService.promptReLogin()
            : throwError(() => refreshError)
        ),
        // Must follow catchError so it also cancels the replacement re-login
        // observable, not only the initial refresh request.
        takeUntil(this.authService.onLogout()),
        finalize(() => {
          this.tokenRefresh$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }

    return this.tokenRefresh$;
  }
}
